import * as React from 'react';
import { Button, Spinner, Typography } from '@/components/common';
import { DetectedPdfField, PdfPageViewport } from '@/interfaces';
import { detectPdfFields } from '@/components/screens/lawyerapp/cases/case-clients/send-document/configure-fields/lib/pdfFieldDetection';
import { isFillableFieldType } from './lib/backendPdfFields';
import { ClientSignFieldOverlay } from './ClientSignFieldOverlay';
import {
	ClientSignFieldsMap,
	ClientSignFieldValue,
} from './hooks/useClientSignFields';

const PDFJS_VERSION = '3.11.174';
const PDFJS_SCRIPT_URL = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${PDFJS_VERSION}/pdf.min.js`;
const PDFJS_WORKER_URL = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${PDFJS_VERSION}/pdf.worker.min.js`;
const PAPER_CONTENT_WIDTH = 720;

const getProxyUrl = (originalUrl: string): string => {
	if (
		originalUrl.includes('/api/') ||
		!originalUrl.includes('s3.us-east-1.amazonaws.com')
	) {
		return originalUrl;
	}
	return `/api/proxy/s3-proxy?url=${encodeURIComponent(originalUrl)}`;
};

declare global {
	interface Window {
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		pdfjsLib: any;
	}
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const loadPdfJs = async (): Promise<any> => {
	if (window.pdfjsLib) {
		window.pdfjsLib.GlobalWorkerOptions.workerSrc = PDFJS_WORKER_URL;
		return window.pdfjsLib;
	}

	return new Promise((resolve, reject) => {
		const script = document.createElement('script');
		script.src = PDFJS_SCRIPT_URL;
		script.onload = () => {
			window.pdfjsLib.GlobalWorkerOptions.workerSrc = PDFJS_WORKER_URL;
			resolve(window.pdfjsLib);
		};
		script.onerror = reject;
		document.head.appendChild(script);
	});
};

const findScrollParent = (node: HTMLElement | null): HTMLElement | null => {
	let el = node?.parentElement ?? null;
	while (el) {
		const { overflowY } = window.getComputedStyle(el);
		if (overflowY === 'auto' || overflowY === 'scroll') return el;
		el = el.parentElement;
	}
	return null;
};

export interface ClientSignPdfCanvasProps {
	pdfUrl: string;
	fields: ClientSignFieldsMap;
	onFieldValueChange: (fieldId: string, value: ClientSignFieldValue) => void;
	onSignatureClick?: (fieldId: string) => void;
	mismatchedFieldIds?: string[];
	focusedFieldId?: string | null;
	settingsByFieldId?: Record<string, string>;
	onApplySuggestion?: (fieldId: string) => void;
	onFieldFocus?: (fieldId: string) => void;
	onFieldBlur?: (fieldId: string) => void;
	presetFields?: DetectedPdfField[] | null;
	onFieldsDetected?: (fields: DetectedPdfField[]) => void;
	onDetectionError?: () => void;
	onRenderError?: (message: string) => void;
	zoomScale?: number;
	onNumPagesChange?: (numPages: number) => void;
	onCurrentPageChange?: (pageIndex: number) => void;
	baseContentWidth?: number;
	minOverlayHeight?: number;
}

export interface ClientSignPdfCanvasHandle {
	scrollToPage: (pageIndex: number) => void;
	scrollToField: (fieldId: string) => void;
	focusField: (fieldId: string) => void;
}

export const ClientSignPdfCanvas = React.forwardRef<
	ClientSignPdfCanvasHandle,
	ClientSignPdfCanvasProps
>(function ClientSignPdfCanvas(
	{
		pdfUrl,
		fields,
		onFieldValueChange,
		onSignatureClick,
		mismatchedFieldIds = [],
		focusedFieldId = null,
		settingsByFieldId = {},
		onApplySuggestion,
		onFieldFocus,
		onFieldBlur,
		presetFields,
		onFieldsDetected,
		onDetectionError,
		onRenderError,
		zoomScale = 1,
		onNumPagesChange,
		onCurrentPageChange,
		baseContentWidth = PAPER_CONTENT_WIDTH,
		minOverlayHeight,
	},
	ref
) {
	const [isLoading, setIsLoading] = React.useState(true);
	const [error, setError] = React.useState<string | null>(null);
	const [loadNonce, setLoadNonce] = React.useState(0);
	const [pdfDocument, setPdfDocument] = React.useState<any>(null);
	const [numPages, setNumPages] = React.useState(0);
	const [pageViewports, setPageViewports] = React.useState<PdfPageViewport[]>(
		[]
	);
	const [detectedFields, setDetectedFields] = React.useState<
		DetectedPdfField[]
	>([]);

	const [pagesContainerNode, setPagesContainerNode] =
		React.useState<HTMLDivElement | null>(null);

	const canvasRefs = React.useRef<Map<number, HTMLCanvasElement>>(new Map());
	const pageViewportInstances = React.useRef<Map<number, any>>(new Map());
	const overlayRefs = React.useRef<Map<string, HTMLElement>>(new Map());
	const pageContainerRefs = React.useRef<Map<number, HTMLDivElement>>(
		new Map()
	);

	React.useImperativeHandle(
		ref,
		() => ({
			scrollToPage: (pageIndex: number) => {
				pageContainerRefs.current
					.get(pageIndex)
					?.scrollIntoView({ behavior: 'smooth', block: 'start' });
			},
			scrollToField: (fieldId: string) => {
				overlayRefs.current
					.get(fieldId)
					?.scrollIntoView({ behavior: 'smooth', block: 'center' });
			},
			focusField: (fieldId: string) => {
				const node = overlayRefs.current.get(fieldId);
				if (!node) return;
				node.scrollIntoView({ behavior: 'smooth', block: 'center' });
				const target = node.matches('input, button')
					? node
					: node.querySelector<HTMLElement>('input, button');
				target?.focus({ preventScroll: true });
			},
		}),
		[]
	);

	React.useEffect(() => {
		if (!pdfUrl) return undefined;

		let isCancelled = false;

		const loadPdf = async (): Promise<void> => {
			try {
				setIsLoading(true);
				setError(null);

				const pdfjsLib = await loadPdfJs();
				const loadingTask = pdfjsLib.getDocument(getProxyUrl(pdfUrl));
				const pdf = await loadingTask.promise;

				if (isCancelled) return;

				setPdfDocument(pdf);
				setNumPages(pdf.numPages);
				onNumPagesChange?.(pdf.numPages);
			} catch (err) {
				console.error('Error loading PDF:', err);
				if (!isCancelled) {
					const message = 'The PDF could not be displayed. Please try again.';
					setError(message);
					onRenderError?.(message);
				}
			} finally {
				if (!isCancelled) setIsLoading(false);
			}
		};

		loadPdf();

		return () => {
			isCancelled = true;
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [pdfUrl, loadNonce]);

	React.useEffect(() => {
		if (!pdfDocument) return undefined;

		let isCancelled = false;

		const fieldsSource = presetFields
			? Promise.resolve(presetFields)
			: detectPdfFields(pdfDocument);

		fieldsSource
			.then((nextFields) => {
				if (isCancelled) return;
				const fillableFields = nextFields.filter((field) =>
					isFillableFieldType(field.fieldType)
				);
				setDetectedFields(fillableFields);
				onFieldsDetected?.(fillableFields);
			})
			.catch((err) => {
				console.error('Error detecting PDF fields:', err);
				if (!isCancelled) onDetectionError?.();
			});

		return () => {
			isCancelled = true;
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [pdfDocument, presetFields]);

	React.useEffect(() => {
		if (!pdfDocument || !pagesContainerNode) return undefined;

		let isCancelled = false;
		const renderTasks: { cancel: () => void }[] = [];

		const renderPages = async (): Promise<void> => {
			try {
				const containerWidth = baseContentWidth;

				for (
					let pageIndex = 0;
					pageIndex < pdfDocument.numPages;
					pageIndex += 1
				) {
					// eslint-disable-next-line no-await-in-loop
					const page = await pdfDocument.getPage(pageIndex + 1);
					if (isCancelled) return;

					const baseViewport = page.getViewport({ scale: 1 });
					const fitScale = containerWidth / baseViewport.width;
					const viewport = page.getViewport({
						scale: fitScale * zoomScale,
					});

					const canvas = canvasRefs.current.get(pageIndex);
					if (!canvas) continue;

					const context = canvas.getContext('2d');
					const outputScale = window.devicePixelRatio || 1;

					canvas.width = Math.floor(viewport.width * outputScale);
					canvas.height = Math.floor(viewport.height * outputScale);
					canvas.style.width = `${Math.floor(viewport.width)}px`;
					canvas.style.height = `${Math.floor(viewport.height)}px`;

					if (outputScale !== 1 && context) {
						context.setTransform(outputScale, 0, 0, outputScale, 0, 0);
					}

					const renderTask = page.render({ canvasContext: context, viewport });
					renderTasks.push(renderTask);
					// eslint-disable-next-line no-await-in-loop
					await renderTask.promise;

					pageViewportInstances.current.set(pageIndex, viewport);

					if (!isCancelled) {
						const pageEntry: PdfPageViewport = {
							pageIndex,
							width: viewport.width,
							height: viewport.height,
						};
						setPageViewports((current) => {
							const without = current.filter(
								(entry) => entry.pageIndex !== pageIndex
							);
							return [...without, pageEntry].sort(
								(a, b) => a.pageIndex - b.pageIndex
							);
						});
					}
				}
			} catch (err) {
				console.error('Error rendering PDF:', err);
				if (!isCancelled) {
					const message = 'The PDF could not be displayed. Please try again.';
					setError(message);
					onRenderError?.(message);
				}
			}
		};

		renderPages();

		return () => {
			isCancelled = true;
			renderTasks.forEach((task) => task.cancel());
		};
	}, [pdfDocument, pagesContainerNode, zoomScale, baseContentWidth]);

	React.useEffect(() => {
		if (!pagesContainerNode || numPages === 0) return undefined;

		const root = findScrollParent(pagesContainerNode);
		const ratios = new Map<number, number>();

		const observer = new IntersectionObserver(
			(entries) => {
				entries.forEach((entry) => {
					const pageIndex = Number(
						(entry.target as HTMLElement).dataset.pageIndex
					);
					ratios.set(pageIndex, entry.intersectionRatio);
				});

				let bestPageIndex = 0;
				let bestRatio = -1;
				ratios.forEach((ratio, pageIndex) => {
					if (ratio > bestRatio) {
						bestRatio = ratio;
						bestPageIndex = pageIndex;
					}
				});
				onCurrentPageChange?.(bestPageIndex);
			},
			{ root, threshold: [0, 0.25, 0.5, 0.75, 1] }
		);

		pageContainerRefs.current.forEach((node) => observer.observe(node));

		return () => observer.disconnect();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [pagesContainerNode, numPages]);

	const fieldsByPage = React.useMemo(() => {
		const map = new Map<number, DetectedPdfField[]>();
		detectedFields.forEach((field) => {
			const bucket = map.get(field.pageIndex) ?? [];
			bucket.push(field);
			map.set(field.pageIndex, bucket);
		});
		return map;
	}, [detectedFields]);

	const handleOverlayRef = React.useCallback(
		(fieldId: string, node: HTMLElement | null) => {
			if (node) overlayRefs.current.set(fieldId, node);
			else overlayRefs.current.delete(fieldId);
		},
		[]
	);

	if (error) {
		return (
			<div className="flex flex-col items-center justify-center gap-3 h-40 w-full">
				<Typography type="regular-14" className="text-main-red">
					{error}
				</Typography>
				<Button
					type="button"
					decoration="primary"
					size="fit"
					label="Retry"
					onClick={() => {
						setPdfDocument(null);
						setError(null);
						setLoadNonce((value) => value + 1);
					}}
				/>
			</div>
		);
	}

	if (isLoading || !pdfDocument) {
		return (
			<div className="flex items-center justify-center h-40 w-full">
				<Spinner />
			</div>
		);
	}

	return (
		<div
			ref={setPagesContainerNode}
			className="flex w-max min-w-full flex-col items-center gap-2 bg-[#F4F0FA] lg:bg-transparent"
		>
			{Array.from({ length: numPages }).map((_, pageIndex) => {
				const viewportMeta = pageViewports.find(
					(v) => v.pageIndex === pageIndex
				);
				const pageFields = fieldsByPage.get(pageIndex) ?? [];
				const pageViewport = pageViewportInstances.current.get(pageIndex);
				const pageWidth = pageViewport?.width ?? viewportMeta?.width;
				const pageHeight = pageViewport?.height ?? viewportMeta?.height;

				return (
					<div
						key={pageIndex}
						ref={(node) => {
							if (node) pageContainerRefs.current.set(pageIndex, node);
							else pageContainerRefs.current.delete(pageIndex);
						}}
						className="relative"
						style={{
							width: pageWidth,
							minHeight: pageHeight,
						}}
						data-page-index={pageIndex}
					>
						<canvas
							ref={(node) => {
								if (node) canvasRefs.current.set(pageIndex, node);
								else canvasRefs.current.delete(pageIndex);
							}}
							className="block"
						/>

						{pageViewport &&
							pageFields.map((field) => {
								const [rx1, ry1, rx2, ry2] =
									pageViewport.convertToViewportRectangle([
										field.rect.x,
										field.rect.y,
										field.rect.x + field.rect.width,
										field.rect.y + field.rect.height,
									]);
								const left = Math.min(rx1, rx2);
								const rawTop = Math.min(ry1, ry2);
								const overlayWidth = Math.abs(rx2 - rx1);
								const overlayHeight = Math.abs(ry2 - ry1);

								return (
									<ClientSignFieldOverlay
										key={field.id}
										field={field}
										state={fields[field.id]}
										left={left}
										top={rawTop}
										width={overlayWidth}
										height={overlayHeight}
										onValueChange={onFieldValueChange}
										onSignatureClick={onSignatureClick}
										onOverlayRef={handleOverlayRef}
										isMismatch={mismatchedFieldIds.includes(field.id)}
										settingsValue={settingsByFieldId[field.id]}
										showSuggestion={focusedFieldId === field.id}
										onApplySuggestion={() => onApplySuggestion?.(field.id)}
										onFocusField={onFieldFocus}
										onBlurField={onFieldBlur}
										minOverlayHeight={minOverlayHeight}
									/>
								);
							})}
					</div>
				);
			})}
		</div>
	);
});

export default ClientSignPdfCanvas;
